import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useDialog } from '../../hooks/useDialog'
import { usePagination } from '../../hooks/usePagination'
import catalogService from '../../services/catalogService'
import { FoodGroup, FoodGroupPayload } from '../../types/catalog'
import { Button } from '../../components/ui/Button'
import { Dialog } from '../../components/ui/Dialog'
import { Pagination } from '../../components/ui/Pagination'
import { FoodGroupForm } from './FoodGroupForm'
import {
  PageHeader,
  PageMeta,
  PageEyebrow,
  PageTitle,
  TableWrapper,
  StyledTable,
  THead,
  TH,
  EmptyState,
  TableSkeleton,
} from '../../components/ui/Table'
import { useConfirm } from '../../hooks/useConfirm'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { FoodGroupRow } from './FoodGroupRow'

export function FoodGroupsPage() {
  const { token } = useAuth()
  const { page, perPage, setPage } = usePagination()
  const dialog = useDialog<FoodGroup>()

  const [items, setItems] = useState<FoodGroup[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [payload, setPayload] = useState<FoodGroupPayload | null>(null)

  const svc = useMemo(() => catalogService(token), [token])
  const { foodGroups } = svc

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await foodGroups.list(page, perPage)
      setItems(res.items)
      setTotal(res.total)
    } finally {
      setLoading(false)
    }
  }, [page, perPage, foodGroups])

  useEffect(() => {
    load()
  }, [load])

  const handleSave = useCallback(async () => {
    if (!payload) return
    setSaving(true)
    try {
      if (dialog.data) {
        await svc.foodGroups.update(dialog.data.id, payload)
      } else {
        await svc.foodGroups.create(payload)
      }
      dialog.close()
      load()
    } finally {
      setSaving(false)
    }
  }, [dialog, payload, svc, load])

  const confirmDialog = useConfirm()

  const handleDelete = useCallback(
    (item: FoodGroup) => {
      confirmDialog.confirm(
        'Delete food group',
        `Are you sure you want to delete "${item.translations.find(t => t.locale === 'en')?.name ?? item.code}"? This action cannot be undone.`,
        async () => {
          await svc.foodGroups.delete(item.id)
          load()
        }
      )
    },
    [confirmDialog, svc, load]
  )

  return (
    <>
      <PageHeader>
        <PageMeta>
          <PageEyebrow>Catalog</PageEyebrow>
          <PageTitle>Food Groups</PageTitle>
        </PageMeta>
        <Button onClick={() => dialog.open()}>+ Add food group</Button>
      </PageHeader>

      {loading ? (
        <TableSkeleton cols={4} />
      ) : (
        <>
          <TableWrapper>
            <StyledTable>
              <THead>
                <tr>
                  <TH>Color</TH>
                  <TH>Code</TH>
                  <TH>English</TH>
                  <TH>Spanish</TH>
                  <TH>Position</TH>
                  <TH />
                </tr>
              </THead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <EmptyState message="No food groups yet." />
                    </td>
                  </tr>
                )}
                {items.map(item => (
                  <FoodGroupRow
                    key={item.id}
                    item={item}
                    onEdit={dialog.open}
                    onDelete={handleDelete}
                  />
                ))}
              </tbody>
            </StyledTable>
          </TableWrapper>
          <Pagination page={page} total={total} perPage={perPage} onChange={setPage} />
        </>
      )}

      <Dialog
        open={dialog.isOpen}
        title={dialog.data ? 'Edit food group' : 'Add food group'}
        onClose={dialog.close}
        onConfirm={handleSave}
        confirmLabel={dialog.data ? 'Save changes' : 'Add food group'}
        confirmLoading={saving}
        confirmDisabled={!payload?.code?.trim()}
      >
        <FoodGroupForm initial={dialog.data} onChange={setPayload} />
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.state.open}
        title={confirmDialog.state.title}
        message={confirmDialog.state.message}
        confirmLabel="Delete"
        onConfirm={confirmDialog.state.onConfirm}
        onCancel={confirmDialog.cancel}
        loading={confirmDialog.loading}
        variant="danger"
      />
    </>
  )
}
